/**
 * ContactQuestion Component
 * 
 * Multi-field form for collecting contact information.
 * Fields:
 * - Email (required)
 * - First Name (required)
 * - Business Name (optional)
 * 
 * Features:
 * - Real-time validation
 * - Field-specific error messages
 * - Accessible form structure
 */

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Input } from '../UI/Input';
import { Question } from '../../types/funnel.types';
import { isValidEmail } from '../../utils/validation';

// ============================================
// TYPES
// ============================================

interface ContactData {
  email?: string;
  name?: string;
  'business-name'?: string;
}

interface ContactQuestionProps {
  /** Question data */
  question: Question;
  /** Current form values */
  value: ContactData;
  /** Change handler */
  onChange: (value: ContactData) => void;
  /** Errors by field */
  errors?: Record<string, string>;
  /** Set field error */
  setFieldError?: (field: string, error: string) => void;
}

// ============================================
// ANIMATION VARIANTS
// ============================================

const fieldVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: (index: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: 0.1 + index * 0.1,
      duration: 0.3,
    },
  }),
};

// ============================================
// COMPONENT
// ============================================

/**
 * ContactQuestion - Multi-field contact form
 */
export const ContactQuestion: React.FC<ContactQuestionProps> = ({
  question,
  value,
  onChange,
  errors = {},
  setFieldError,
}) => {
  // Track which fields have been touched (for validation timing)
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  // Handle field change
  const handleFieldChange = (fieldId: string, fieldValue: string) => {
    onChange({
      ...value,
      [fieldId]: fieldValue,
    });

    // Clear error when user starts typing
    if (errors[fieldId] && setFieldError) {
      setFieldError(fieldId, '');
    }
  };

  // Handle field blur (validate on blur)
  const handleFieldBlur = (fieldId: string) => {
    setTouched(prev => ({ ...prev, [fieldId]: true }));

    // Validate the field
    const fieldValue = value[fieldId as keyof ContactData] || '';
    const fieldDef = question.fields?.find(f => f.id === fieldId);

    if (fieldDef && setFieldError) {
      // Required validation
      if (fieldDef.validation?.required && !fieldValue.trim()) {
        setFieldError(fieldId, 'This field is required');
        return;
      }

      // Email validation
      if (fieldDef.type === 'email' && fieldValue && !isValidEmail(fieldValue)) {
        setFieldError(fieldId, 'Please enter a valid email address');
        return;
      }

      // Min length validation
      if (fieldDef.validation?.minLength && fieldValue.length < fieldDef.validation.minLength) {
        setFieldError(fieldId, `Please enter at least ${fieldDef.validation.minLength} characters`);
        return;
      }

      // Clear error if validation passes
      setFieldError(fieldId, '');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="space-y-6"
    >
      {question.fields?.map((field, index) => (
        <motion.div
          key={field.id}
          custom={index}
          variants={fieldVariants}
          initial="hidden"
          animate="visible"
        >
          <Input
            type={field.type}
            label={field.label}
            placeholder={field.placeholder}
            value={value[field.id as keyof ContactData] || ''}
            onChange={(e) => handleFieldChange(field.id, e.target.value)}
            onBlur={() => handleFieldBlur(field.id)}
            error={touched[field.id] ? errors[field.id] : undefined}
            required={field.validation?.required}
            leftIcon={getFieldIcon(field.type)}
          />
        </motion.div>
      ))}

      {/* Privacy notice */}
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="text-xs text-text-secondary text-center mt-6"
      >
        <LockIcon /> Your information is secure and will never be shared or sold.
      </motion.p>
    </motion.div>
  );
};

ContactQuestion.displayName = 'ContactQuestion';

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Get appropriate icon for field type
 */
function getFieldIcon(fieldType: string): React.ReactNode {
  switch (fieldType) {
    case 'email':
      return <EmailIcon />;
    case 'tel':
      return <PhoneIcon />;
    default:
      return <UserIcon />;
  }
}

// ============================================
// ICON COMPONENTS
// ============================================

const EmailIcon: React.FC = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"
    />
  </svg>
);

const UserIcon: React.FC = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z"
    />
  </svg>
);

const PhoneIcon: React.FC = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z"
    />
  </svg>
);

const LockIcon: React.FC = () => (
  <svg className="w-3 h-3 inline mr-1" fill="currentColor" viewBox="0 0 20 20">
    <path
      fillRule="evenodd"
      d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
      clipRule="evenodd"
    />
  </svg>
);

export default ContactQuestion;
